import Storage from '@aws-amplify/storage';
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef, useState } from 'react';
import { Button, Col, Form, Row, Spinner, Alert, Image } from 'react-bootstrap';
import { UserProfile } from '../../models/UserProfile';
import { GenericModal } from '../common/generic-modal';
import { UserProfileUIStore } from '../../stores/user-profile-ui-store';

interface LogoUploadProps {
  userProfile: UserProfile;
  userProfileUIStore?: UserProfileUIStore;
  onLogoUploaded: (logoUrl: string) => void;
  onLogoRemoved: () => void;
  onError: (error: string) => void;
}

export const LogoUpload: React.FC<LogoUploadProps> = ({
  userProfile,
  userProfileUIStore,
  onLogoUploaded,
  onLogoRemoved,
  onError,
}) => {
  const inputFile = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      return 'Only PNG and JPG files are supported';
    }

    // Check file size (2MB max)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      return 'File size must not exceed 2MB';
    }

    return null;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      onError(validationError);
      return;
    }

    setError(null);
    setFile(selectedFile);

    // Create preview
    const reader = new FileReader();
    reader.onload = event => {
      setPreview(event.target?.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleUpload = async () => {
    if (!file || !userProfile.id) return;

    setLoading(true);
    setError(null);

    try {
      // Generate unique filename with user ID
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const fileName = `user-logos/${userProfile.id}.${fileExtension}`;

      // Upload to S3
      await Storage.put(fileName, file, {
        contentType: file.type,
        level: 'public',
      });

      // Generate public URL without signed parameters
      // Since files are uploaded to public/ prefix, they should be accessible via direct S3 URL
      const publicUrl = `https://labsmarts-next.s3.amazonaws.com/public/${fileName}`;

      onLogoUploaded(publicUrl);
      setLoading(false);
      handleCancel();
    } catch (uploadError) {
      console.error('Upload error:', uploadError);
      const errorMessage = 'Error uploading file. Please try again.';
      setError(errorMessage);
      onError(errorMessage);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    if (inputFile.current) {
      inputFile.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!userProfile.hasCustomLogo()) return;

    // Confirm deletion
    const confirmed = window.confirm(
      'Are you sure you want to remove your custom logo? This will revert to the default logo.',
    );

    if (!confirmed) return;

    setLoading(true);
    setError(null);

    try {
      // Extract filename from the current logo URL
      const logoUrl = userProfile.customLogoUrl;
      const fileName = logoUrl.split('/').pop()?.split('?')[0]; // Remove query params if any

      if (fileName) {
        // Delete from S3
        await Storage.remove(fileName);
      }

      onLogoRemoved();
      setLoading(false);
    } catch (removeError) {
      console.error('Remove error:', removeError);
      const errorMessage = 'Error removing logo. Please try again.';
      setError(errorMessage);
      onError(errorMessage);
      setLoading(false);
    }
  };

  const handleClick = () =>
    userProfile.canUploadCustomLogo()
      ? inputFile.current?.click()
      : setShowUpgradeModal(true);

  return (
    <div>
      <p className="text-muted">
        Upload your logo that will be displayed in the interface and PDF
        reports. Supported file types are PNG and JPG with a maximum size of
        2MB.
      </p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Form.Group as={Row} className="mb-5">
        <Form.Label column sm={3}>
          Select File:
        </Form.Label>
        <Col sm={9}>
          <div className="d-flex align-items-center">
            <div>
              {!file && (
                <Button
                  variant="outline-primary"
                  disabled={loading}
                  onClick={handleClick}
                  className="me-3"
                >
                  Choose File
                </Button>
              )}
              <small className="text-muted d-block mt-2">
                Upload a new file to replace the current logo.
              </small>
            </div>

            <input
              hidden
              type="file"
              ref={inputFile}
              onChange={handleFileSelect}
              accept="image/png,image/jpeg,image/jpg"
            />

            {file && <span className="text-muted me-3 mr-4">{file.name}</span>}

            {file && !loading && (
              <Button
                variant="success"
                onClick={handleUpload}
                className="me-2 mr-3"
              >
                <FontAwesomeIcon icon={faSave} className="me-2 mr-2 pt-1" />
                Save
                {loading && (
                  <Spinner className="ms-2" animation="border" size="sm" />
                )}
              </Button>
            )}

            {file && (
              <Button
                variant="outline-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2 mr-3"
                    />
                    Loading...
                  </>
                ) : (
                  'Cancel'
                )}
              </Button>
            )}
          </div>
        </Col>
      </Form.Group>

      {preview && (
        <Form.Group as={Row} className="mb-5">
          <Form.Label column sm={3}>
            Preview:
          </Form.Label>
          <Col sm={9}>
            <div
              style={{
                maxWidth: '300px',
                maxHeight: '150px',
                overflow: 'hidden',
              }}
            >
              <Image
                src={preview}
                alt="Logo Preview"
                fluid
                style={{ maxHeight: '150px', objectFit: 'contain' }}
              />
            </div>
          </Col>
        </Form.Group>
      )}

      {userProfile.hasCustomLogo() && (
        <Form.Group as={Row} className="mb-5">
          <Form.Label column sm={3}>
            Current Logo:
          </Form.Label>
          <Col sm={9}>
            <div
              style={{
                maxWidth: '300px',
                maxHeight: '150px',
                overflow: 'hidden',
              }}
            >
              <Image
                key={`${userProfile.customLogoUrl}-${
                  userProfileUIStore?.logoUpdateTrigger || 0
                }`}
                src={userProfile.customLogoUrl}
                alt="Current Logo"
                fluid
                style={{ maxHeight: '150px', objectFit: 'contain' }}
              />
            </div>
            <div className="mt-3">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRemoveLogo}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2 mr-3"
                    />
                    Loading...
                  </> 
                ) : (
                  'Revert to LabSmarts Logo'
                )}
              </Button>
            </div>
          </Col>
        </Form.Group>
      )}

      <GenericModal
        show={showUpgradeModal}
        title="Custom Logo Feature Not Available"
        text="Custom Logo feature not enabled."
        link="https://labsmarts.com/custom-logo-upgrade"
        linkText="Click here to upgrade."
        onHide={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};
